import React, { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signupSchema } from '../../lib/validation';
import { z } from 'zod';

interface SignupFormProps {
  onSuccess: () => void;
  onFailure: (message: string) => void;
}

export const SignupForm: React.FC<SignupFormProps> = ({ onSuccess, onFailure }) => {
  const searchParams = useSearchParams();
  const refFromUrl = searchParams?.get('ref')?.trim() || undefined;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0); // 0: weak, 1: medium, 2: strong
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const router = useRouter();

  const getPasswordStrength = (pw: string) => {
    let strength = 0;
    if (pw.length >= 8) strength++;
    if (/[A-Za-z]/.test(pw) && /\d/.test(pw)) strength++;
    if (/[^A-Za-z0-9]/.test(pw)) strength++; // Check for special characters
    return strength;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    onFailure(''); // Clear general error
    setLoading(true);

    const validationResult = signupSchema.safeParse({
      email,
      password,
      confirmPassword,
    });

    if (!validationResult.success) {
      setLoading(false);
      validationResult.error.issues.forEach(issue => {
        if (issue.path[0] === 'email') setEmailError(issue.message);
        if (issue.path[0] === 'password') setPasswordError(issue.message);
        if (issue.path[0] === 'confirmPassword') setConfirmPasswordError(issue.message);
      });
      onFailure('Please correct the errors in the form.');
      return;
    }

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, ...(refFromUrl ? { ref: refFromUrl } : {}) }),
      });
      const data = await res.json();
      setLoading(false);

      if (!res.ok) {
        onFailure(data.error || 'Registration failed');
      } else {
        onSuccess();
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'signup_success', {
            event_category: 'Onboarding',
            event_label: 'User Registered Successfully',
          });
        }
        router.push('/welcome');
      }
    } catch (err) {
      console.error('Client-side error during signup:', err);
      setLoading(false);
      onFailure('An unexpected error occurred.');
    }
  };

  return (
    <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 260 }} aria-labelledby="signup-heading">
      <input
        type="email"
        placeholder="Email"
        required
        value={email}
        onChange={e => setEmail(e.target.value)}
        style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px', color: '#333', background: '#fff' }}
        aria-label="Email"
        aria-required="true"
        aria-invalid={!!emailError}
        aria-describedby={emailError ? 'email-error' : undefined}
      />
      {emailError && <div id="email-error" role="alert" style={{ color: 'red', fontSize: '0.8em', marginTop: -8 }}>{emailError}</div>}
      <div style={{ position: 'relative' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder="Password"
          required
          value={password}
          onChange={e => {
            setPassword(e.target.value);
            setPasswordStrength(getPasswordStrength(e.target.value));
          }}
        style={{ padding: '10px', border: '1px solid #999', borderRadius: '4px', paddingRight: '110px', color: '#333', background: '#fff' }}
          aria-label="Password"
          aria-required="true"
          aria-invalid={!!passwordError}
          aria-describedby={passwordError ? 'password-error' : password ? 'password-strength-info' : undefined}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: '1px solid #999', // Updated border color for visibility
            padding: '4px 8px', // Added padding
            borderRadius: '4px', // Added border radius
            color: '#333', // Darker text color for contrast
            cursor: 'pointer',
            zIndex: 1 // Ensure button is on top
          }}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </div>
      {passwordError && <div id="password-error" role="alert" style={{ color: 'red', fontSize: '0.8em', marginTop: -8 }}>{passwordError}</div>}
      {password && (
        <div id="password-strength-info" aria-live="polite" style={{ fontSize: '0.8em', marginTop: -8, marginBottom: 8, color: passwordStrength === 0 ? 'red' : passwordStrength === 1 ? '#CC6600' : 'green' }}>
          Password Strength: {passwordStrength === 0 ? 'Weak' : passwordStrength === 1 ? 'Medium' : 'Strong'}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder="Confirm Password"
          required
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
        style={{ padding: '10px', border: '1px solid #999', borderRadius: '4px', paddingRight: '110px', color: '#333', background: '#fff' }}
          aria-label="Confirm Password"
          aria-required="true"
          aria-invalid={!!confirmPasswordError}
          aria-describedby={confirmPasswordError ? 'confirm-password-error' : undefined}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          style={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: '1px solid #999', // Updated border color for visibility
            padding: '4px 8px', // Added padding
            borderRadius: '4px', // Added border radius
            color: '#333', // Darker text color for contrast
            cursor: 'pointer',
            zIndex: 1 // Ensure button is on top
          }}
          aria-label={showPassword ? 'Hide confirm password' : 'Show confirm password'}
        >
          {showPassword ? 'Hide' : 'Show'}
        </button>
      </div>
      {confirmPasswordError && <div id="confirm-password-error" role="alert" style={{ color: 'red', fontSize: '0.8em', marginTop: -8 }}>{confirmPasswordError}</div>}
      <button disabled={loading} type="submit" style={{ padding: '10px 20px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1em' }}>{loading ? 'Signing up...' : 'Sign up'}</button>
      {/* Social sign-in buttons are rendered at page level; avoid duplicating them here */}
    </form>
  );
};
